import { useState, useEffect, useRef } from 'react';
import {
  makeStyles,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Paper,
  Collapse,
  TextField,
  Button,
  Avatar,
} from '@material-ui/core';
import { Close as CloseIcon, Android as BotIcon } from '@material-ui/icons';
import { PullRequestComment } from '../../types';

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.type === 'dark'
      ? theme.palette.background.default
      : theme.palette.grey[100],
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  content: {
    maxHeight: 400,
    overflow: 'auto',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
  },
  footer: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.type === 'dark'
      ? theme.palette.background.default
      : theme.palette.grey[50],
    borderTop: `1px solid ${theme.palette.divider}`,
    gap: theme.spacing(1),
  },
  footerActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
  },
  closeButton: {
    padding: theme.spacing(0.5),
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  error: {
    padding: theme.spacing(2),
    color: theme.palette.error.main,
  },
  noComments: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  comment: {
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  avatar: {
    width: 32,
    height: 32,
  },
  commentContent: {
    flex: 1,
    minWidth: 0,
  },
  commentHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  authorName: {
    fontWeight: 500,
    fontSize: '0.875rem',
  },
  botBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    backgroundColor: theme.palette.type === 'dark'
      ? theme.palette.grey[700]
      : theme.palette.grey[200],
    color: theme.palette.text.secondary,
    padding: '1px 6px',
    borderRadius: 4,
    fontSize: '0.7rem',
  },
  timestamp: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
  },
  commentBody: {
    fontSize: '0.875rem',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    '& code': {
      backgroundColor: theme.palette.type === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.grey[100],
      padding: '2px 4px',
      borderRadius: 4,
      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
      fontSize: '0.8rem',
    },
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      fontSize: '0.875rem',
    },
  },
}));

interface CommentsViewerProps {
  comments: PullRequestComment[] | null;
  loading: boolean;
  error: string | null;
  open: boolean;
  onClose: () => void;
  onAddComment: (body: string) => Promise<void>;
  submitting: boolean;
}

export const CommentsViewer = ({
  comments,
  loading,
  error,
  open,
  onClose,
  onAddComment,
  submitting,
}: CommentsViewerProps) => {
  const classes = useStyles();
  const [newComment, setNewComment] = useState('');
  const textFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && textFieldRef.current) {
      // Small delay to allow Collapse animation to start
      setTimeout(() => {
        textFieldRef.current?.focus();
      }, 100);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (newComment.length >= 2) {
      await onAddComment(newComment);
      setNewComment('');
    }
  };

  const handleCancel = () => {
    setNewComment('');
    onClose();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const canSubmit = newComment.length >= 2 && !submitting;

  return (
    <Collapse in={open}>
      <Paper className={classes.container} elevation={0}>
        <Box className={classes.header}>
          <Typography variant="subtitle2">
            Comments ({comments?.length || 0})
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            className={classes.closeButton}
            aria-label="Close comments"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box className={classes.content}>
          {loading && (
            <Box className={classes.loading}>
              <CircularProgress size={24} />
              <Typography variant="body2" style={{ marginLeft: 8 }}>
                Loading comments...
              </Typography>
            </Box>
          )}

          {error && (
            <Box className={classes.error}>
              <Typography variant="body2">{error}</Typography>
            </Box>
          )}

          {/* Empty state removed - count already shown in header */}

          {!loading && !error && comments && comments.map(comment => (
            <Box key={comment.id} className={classes.comment}>
              <Avatar
                src={comment.author.avatarUrl}
                alt={comment.author.login}
                className={classes.avatar}
              />
              <Box className={classes.commentContent}>
                <Box className={classes.commentHeader}>
                  <Typography className={classes.authorName}>
                    {comment.author.login}
                  </Typography>
                  {comment.isBot && (
                    <Box className={classes.botBadge}>
                      <BotIcon style={{ fontSize: 12 }} />
                      bot
                    </Box>
                  )}
                  <Typography className={classes.timestamp}>
                    {formatDate(comment.createdAt)}
                  </Typography>
                </Box>
                <Typography className={classes.commentBody}>
                  {comment.body}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Box className={classes.footer}>
          <TextField
            inputRef={textFieldRef}
            placeholder="Add a comment..."
            multiline
            rows={2}
            variant="outlined"
            size="small"
            fullWidth
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            className={classes.textField}
            disabled={submitting}
          />
          <Box className={classes.footerActions}>
            <Button
              size="small"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {submitting ? <CircularProgress size={16} color="inherit" /> : 'Comment'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Collapse>
  );
};
